import { useMemo } from "react";
import { useQuestions } from "src/context/QuestionsProvider";
import SingleChoice from "./components/SingleChoice/SingleChoice";
import MultipleChoice from "./components/MultipleChoice";
import QuestionText from "./components/QuestionText";
import QuestionNumber from "./QuestionNumber";
import ProgressProjection from "src/pages/ProgressProjection";
import { QuestionTypes } from "../../constants";
import HeightWeight from "src/pages/Onboarding/HeightWeight";
import DateOfBirth from "src/pages/Onboarding/DateOfBirth";
import ResultInformation from "src/pages/ResultInformation";
import Reviews from "src/pages/Reviews";
import CancellationMessage from "./components/Cancellation/Message";
import CancellationReview from "./components/Cancellation/Review";
import MessageImage from "./components/MessageImage";
import EdOptions from "./components/EdOptions";
import Message from "./components/Message";
import ShippingAddress from "src/pages/Onboarding/ShippingAddress";
import IDVerification from "src/pages/VouchedVerification/IDVerification";
import Payment from "src/pages/Onboarding/Payment";
import PaymentConfirmation from "src/pages/Onboarding/PaymentConfirmation";
import PricesProvider from "src/context/PricesProvider";
import InsuranceUpload from "src/pages/InsuranceUpload";
import PharmacySearch from "src/pages/PharmacySearch";
import ABTest from "src/components/ABTest";
import CustomSingleChoice from "./components/CustomSingleChoice";

const QuestionBody = () => {
  const questionType = useQuestions((q) => q.question_type);
  const questionTag = useQuestions((q) => q.tag);

  const questionBody = useMemo(() => {
    if (questionType === QuestionTypes.MULTI_CHOICE) {
      return <MultipleChoice />;
    }

    if (questionType === QuestionTypes.TEXT) {
      if (questionTag === "ALLERGY") {
        return (
          <ABTest
            control={<QuestionText />}
            variantA={<QuestionText />}
            variantB={<CustomSingleChoice />}
            testName="New allergies UI"
          />
        );
      }

      return <QuestionText />;
    }

    if (questionType === QuestionTypes.SINGLE_CHOICE) {
      return <SingleChoice />;
    }

    if (questionType === QuestionTypes.NUMBER) {
      return <QuestionNumber />;
    }

    if (questionType === QuestionTypes.MESSAGE) {
      return <Message />;
    }

    if (questionType === QuestionTypes.PROGRESS_PROJECTION) {
      return <ProgressProjection />;
    }

    if (questionType === QuestionTypes.HEIGHT_WEIGHT) {
      return <HeightWeight />;
    }

    if (questionType === QuestionTypes.BIRTHDAY) {
      return <DateOfBirth />;
    }

    if (questionType === QuestionTypes.GLP1_PRELIMINARY_RESULTS) {
      return <ResultInformation />;
    }

    if (questionType === QuestionTypes.GLP1_REVIEWS) {
      return <Reviews />;
    }

    if (questionType === QuestionTypes.CANCELLATION_MESSAGE) {
      return <CancellationMessage />;
    }

    if (questionType === QuestionTypes.CANCELLATION_REVIEW) {
      return <CancellationReview />;
    }

    if (questionType === QuestionTypes.MESSAGE_IMAGE) {
      return <MessageImage />;
    }

    if (questionType === QuestionTypes.ED_OPTIONS) {
      return <EdOptions />;
    }

    if (questionType === QuestionTypes.SHIPPING_INFORMATION) {
      return <ShippingAddress />;
    }

    if (
      questionType === QuestionTypes.ID_VERIFICATION &&
      questionTag === "ID"
    ) {
      return <IDVerification key="id" type="id" fileName="front-of-card" />;
    }

    if (
      questionType === QuestionTypes.ID_VERIFICATION &&
      questionTag === "SELFIE"
    ) {
      return (
        <IDVerification key="selfie" type="selfie" fileName="self-portrait" />
      );
    }

    if (
      questionType === QuestionTypes.ID_VERIFICATION &&
      questionTag === "INSURANCE-FRONT"
    ) {
      return (
        <InsuranceUpload
          key="insurance"
          cardFace="front"
          fileName="front-of-card"
        />
      );
    }

    if (
      questionType === QuestionTypes.ID_VERIFICATION &&
      questionTag === "INSURANCE-BACK"
    ) {
      return (
        <InsuranceUpload
          key="insurance"
          cardFace="back"
          fileName="back-of-card"
        />
      );
    }

    if (questionType === QuestionTypes.PAYMENT_INFORMATION) {
      return (
        <PricesProvider>
          <Payment />
        </PricesProvider>
      );
    }

    if (questionType === QuestionTypes.PAYMENT_CONFIRMATION) {
      return <PaymentConfirmation />;
    }

    if (questionType === QuestionTypes.PHARMACY_SEARCH) {
      return <PharmacySearch />;
    }

    return null;
  }, [questionTag, questionType]);

  return questionBody;
};

export default QuestionBody;
